
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { navigationToasts } from '@/utils/toastUtils';

export const useNavigationHandlers = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [theme, setTheme] = useState<string>('light');
  const [megaMenuOpen, setMegaMenuOpen] = useState<string | null>(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const menuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSearchOpen: () => setSearchModalOpen(true),
    onEscape: () => {
      setSearchModalOpen(false);
      setMegaMenuOpen(null);
      setIsOpen(false);
    }
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  // Close any open mega menu on route changes for safety
  useEffect(() => {
    setMegaMenuOpen(null);
  }, [location.pathname]);

  const navItems = [
    { label: 'Homepage', href: '/', hasMegaMenu: true },
    { label: 'Products', href: '/products', hasMegaMenu: true },
    { label: 'Portfolio', href: '/portfolio' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Contact Us', href: '/contact' },
    { 
      label: 'Admin Portal', 
      href: '/admin', 
      hasMegaMenu: true,
      adminOnly: true,
      megaMenuItems: [
        { label: 'Dashboard', href: '/admin', description: 'Executive overview and metrics' },
        { label: 'Users & Companies', href: '/admin?tab=users', description: 'Manage users and organisations' },
        { label: 'CRM & Marketing', href: '/admin?tab=crm', description: 'Customer relationship management' },
        { label: 'Analytics & Reports', href: '/admin?tab=analytics', description: 'Performance insights and data' },
        { label: 'Security & Settings', href: '/admin?tab=security', description: 'System security and configuration' }
      ]
    },
    { label: 'Project Wizard', href: '/website-wizard', variant: 'primary' as const }
  ];

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const handleMouseEnterNavItem = (itemLabel: string) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    setMegaMenuOpen(itemLabel);
    startInactivityTimer();
  };

  const handleMouseLeaveNavItem = () => {
    // Grace period so users can cross the gap between the nav link and the
    // mega menu without it closing. The 2s inactivity timer handles "no movement".
    const delay = navigator.maxTouchPoints > 0 ? 600 : 220;
    menuTimeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(null);
    }, delay);
  };

  const clearMenuTimeout = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
  };

  const startInactivityTimer = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    const delay = navigator.maxTouchPoints > 0 ? 5000 : 2000; // 5s for touch, 2s for desktop
    inactivityTimeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(null);
    }, delay);
  };

  const resetInactivityTimer = () => {
    if (megaMenuOpen) {
      startInactivityTimer();
    }
  };

  const clearInactivityTimer = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  };

  const closeMegaMenuImmediately = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    clearInactivityTimer();
    setMegaMenuOpen(null);
  };

  const handleNavigationClick = (href: string) => {
    // Add fresh parameter for wizard navigation if not already present
    let finalHref = href;
    if (href.startsWith("/website-wizard") && !href.includes("fresh=")) {
      const separator = href.includes("?") ? "&" : "?";
      finalHref = `${href}${separator}fresh=true`;
    }
    
    // Use React Router navigation
    navigate(finalHref);
    closeMegaMenuImmediately();
  };

  const handleThemeToggle = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const skipToMain = (e: React.MouseEvent) => {
    e.preventDefault();
    const main = document.getElementById('main-content');
    if (main) {
      main.focus();
      main.scrollIntoView();
    }
  };

  const handleSearchOpen = () => {
    setSearchModalOpen(true);
  };

  const handleSearchClose = () => {
    setSearchModalOpen(false);
  };

  return {
    isOpen,
    setIsOpen,
    mounted,
    theme,
    handleThemeToggle,
    navItems,
    isActive,
    megaMenuOpen,
    searchModalOpen,
    handleMouseEnterNavItem,
    handleMouseLeaveNavItem,
    clearMenuTimeout,
    closeMegaMenuImmediately,
    resetInactivityTimer,
    handleNavigationClick,
    handleSearchOpen,
    handleSearchClose,
    skipToMain
  };
};
